import { get as _get } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useContext,
  useMemo,
  useEffect
} from 'react';
import {
  Input as ChakraInput,
  Text
} from '@chakra-ui/react';
import {
  Field,
  useFormState
} from 'react-final-form';
import useIncrement from 'shared/src/hooks/useIncrement';

import SuiForm from '../../../components/collections/Form';
import Input from '../../../components/elements/Input';

import FieldError from '../FieldError';
import LocalizableTextFieldLabel from './LocalizableTextFieldLabel';
import LocalizationWrapper from './LocalizationWrapper';

const LocalizableTextField = (props) => {
  const {
    name,
    label,
    // validate, // TODO: Compose validate with validateLocale?
    validateLocale,
    fluid = true,
    inputProps,
    optional,
    wrapLabel,
    info,
    chakra,
    ...rest
  } = props;

  const {
    locale, defaultLocale, isDefaultLocale, locales
  } = useContext(LocalizationWrapper.context);

  const formState = useFormState({ values: true });
  const fieldValue = _get(formState.values, name);

  const [key, invalidate] = useIncrement();

  const format = useCallback(
    (dictionary) => {
      if (!dictionary) return undefined; // Handle no form values (is there a better way to do this?)
      return dictionary[locale] || '';
    },
    [locale]
  );

  const parse = useCallback(
    str => ({
      ...fieldValue,
      [locale]: str
    }),
    [locale, fieldValue]
  );

  const validate = useMemo(
    () => {
      if (!validateLocale) return undefined;
      return validateLocale({ locale, locales });
    },
    [locale, locales, validateLocale]
  );

  useEffect(() => {
    invalidate();
  }, [locale]);

  const defaultValue = useMemo(
    () => (fieldValue && fieldValue[defaultLocale]),
    [fieldValue, defaultLocale]
  );

  const FieldCmp = chakra ? React.Fragment : SuiForm.Field;
  const InputCmp = chakra ? ChakraInput : Input;

  return (
    <Field name={name} validate={validate} format={format} parse={parse} info={info} key={key}>
      {({ input, meta }) => {
        // Built-in `pristine` field state doesn't seem to work with nested fields -- latches permanently to `false`
        const pristine = !meta.initial?.[locale] || (meta.initial?.[locale] === input.value);

        return (
          <FieldCmp>
            <LocalizableTextFieldLabel
              label={label}
              optional={optional}
              wrapLabel={wrapLabel}
              locale={locale}
              locales={locales}
            >
              <InputCmp
                {...input}
                fluid={fluid}
                type="text"
                {...rest}
                {...inputProps}
                outline={pristine ? 'none' : '2px solid green !important'}
              />
              {meta.active && !isDefaultLocale && (
                <div>{defaultLocale}: {defaultValue}</div>
              )}
              {info && <Text color="gray.500">{info}</Text>}
              <FieldError name={name} chakra={chakra} />
            </LocalizableTextFieldLabel>
          </FieldCmp>
        );
      }}
    </Field>
  );
};

LocalizableTextField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  validateLocale: PropTypes.func,
  fluid: PropTypes.bool,
  inputProps: PropTypes.object,
  optional: PropTypes.bool,
  wrapLabel: PropTypes.bool,
  info: PropTypes.string,
  chakra: PropTypes.bool,
};


export default LocalizableTextField;
